import { Injectable, Predicate } from '@angular/core'
import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms'
import {
  EbFieldConfig,
  EbFormElementConfig,
  EbFormGroupConfig,
  EbValidatorFnConfig,
  EbValidatorFunctionConfig
} from '../interfaces/eb-form.interface'

type FormElementValidators = {
  validations: ValidatorFn[]
  asyncValidations: AsyncValidatorFn[]
}

const validatorFnFactory = (
  predicate: Predicate<boolean>,
  validator: EbValidatorFunctionConfig
): ValidatorFn => {
  return control =>
    predicate.call(null, [control, validator.arguments?.map(arg => arg?.value)])
      ? [validator.name]
      : null
}

@Injectable({ providedIn: 'root' })
export class EbFormValidatorProviderService {
  processValidation(elementConfig: EbFormElementConfig): FormElementValidators {
    if (elementConfig.type === 'field') {
      return this.processFieldValidation(elementConfig as EbFieldConfig)
    } else if (elementConfig.type === 'group') {
      return this.processGroupValidation(elementConfig as EbFormGroupConfig)
    }
    return {
      validations: [],
      asyncValidations: []
    }
  }

  private processGroupValidation(groupConfig: EbFormGroupConfig): FormElementValidators {
    return {
      validations: this.processEbValidatorFnConfigs(groupConfig.validations),
      asyncValidations: groupConfig.asyncValidations
    }
  }

  private processFieldValidation(fieldConfig: EbFieldConfig): FormElementValidators {
    const validations: ValidatorFn[] = []
    const asyncValidations = fieldConfig.asyncValidations
    if (fieldConfig.required) {
      validations.push(Validators.required)
    }
    validations.push(...(this.processEbValidatorFnConfigs(fieldConfig.validations) || []))
    return {
      validations,
      asyncValidations
    }
  }

  private processEbValidatorFnConfigs(validations: EbValidatorFnConfig[] = []): ValidatorFn[] {
    return validations
      .map(validator => {
        if (validator instanceof Function) {
          return validator
        } else {
          return this.processSingleValidatorConfig(validator)
        }
      })
      .filter(validatorFn => validatorFn)
  }

  private processSingleValidatorConfig(validator: EbValidatorFunctionConfig): ValidatorFn {
    let fn = null
    switch (validator.name) {
      case 'required':
        fn = control => !control.value
        break
      case 'number':
        fn = control => isNaN(control.value)
        break
      case 'minValue':
        fn = (control, args) => control.value < args[0]
        break
      case 'maxValue':
        fn = (control, args) => control.value > args[0]
        break
      case 'maxLength':
        fn = (control, args) => control.value.length > args[0]
        break
      case 'minLength':
        fn = (control, args) => control.value.length < args[0]
        break
      case 'checkValue':
        fn = (control, args) => control.value === args[0]
        break
    }
    if (fn) {
      return validatorFnFactory(fn, validator)
    }
    return undefined
  }
}
